/* storybook-check-ignore */
import * as React from 'react';

// TODO: Label should not be used directly and should stay as an internal utility for bricks form components. Please update the usage of Label.
// eslint-disable-next-line no-restricted-imports
import { Label } from '@opendoor/bricks/core';
import { IAddressInputAddress } from '@opendoor/cinderblocks/growth/AddressInput/AddressInput';
import { AddressInput, IAddressInputProps } from '@opendoor/cinderblocks/growth/AddressInput';

import { startAddressVerification } from '../api/addressInputHelpers';
import { setCookie } from './trackers';
import { CONSUMER_FE_URL, GOOGLE_MAPS_API_KEY, HOMES_PDP_BASE_URL } from '../globals';
import { trackImpression, trackInteraction } from '@opendoor/track/lib/tracker';
import { useFeatureFlag } from '../helpers/hooks';
import { EventTrackingAction, useObservability } from '@opendoor/observability';
import { CTAProps } from '@opendoor/bricks/complex/CtaInput';

type IAddressInputPropsWithoutCallbacks = Omit<
  IAddressInputProps,
  'onSubmit' | 'onChooseManualEntry' | 'googleMapsApiKey' | 'analyticsName'
>;
export interface IProps extends IAddressInputPropsWithoutCallbacks {
  trackingKeywords?: string;
  trackingTaxonomy?: string;
  inputLocation?: string;
  ctaProps: CTAProps;
  hideLabel?: boolean;
  // We only want to display above for sticky CTA mobile
  displayAbove?: boolean;
  placeholderText?: string;
  /**
   * Name of the address input in analytics events, should be unique within the app
   * Convention: {appName}-{addressInputName}
   */
  analyticsName: string;
}

const onSubmit = async (
  address: IAddressInputAddress,
  sendToPdp: boolean,
  trackEvent: ReturnType<typeof useObservability>['trackEvent'],
  _impressionId?: string,
  trackingTaxonomy?: string,
  channel?: string,
  trackingKeywords?: string,
  inputLocation?: string,
) => {
  trackInteraction({ impressionId: _impressionId || 'none', type: 'select' });

  // Setting cookie to know where the customer is coming from in reception-fe for experiments
  if (trackingTaxonomy) {
    setCookie('sourceForTracking', trackingTaxonomy, 1);
  }

  const error = await startAddressVerification({
    address,
    channel,
    trackingKeywords,
    inputLocation,
    onSuccess: (token, pdpUrl) => {
      if (sendToPdp && pdpUrl) {
        trackEvent('inputting_address_success' as EventTrackingAction, 'pdp-redirect');
        window.location.href = `${HOMES_PDP_BASE_URL}${pdpUrl}?utm_source=seller&utm_medium=web&utm_campaign=ae_pdp`;
      } else {
        trackEvent('inputting_address_success' as EventTrackingAction, 'onboarding-redirect');
        window.location.href = `${CONSUMER_FE_URL}/seller-lead/${token}`;
      }
    },
  });
  if (error) {
    window.location.href = '/error';
  }
};

const startManualAddressVerification = (channel?: string, trackingKeywords?: string) => {
  const path = `${CONSUMER_FE_URL}/seller-lead/address-manual`;
  const searchParams = new URLSearchParams();
  if (channel) {
    searchParams.set('channel', channel);
  }
  if (trackingKeywords) {
    searchParams.set('keywords', trackingKeywords);
  }

  const params = searchParams.toString();
  window.location.assign(path + (params ? `?${params}` : ''));
};

export const SellerFlowAddressInput: React.FC<IProps> = ({
  className,
  inputLocation,
  trackingKeywords,
  trackingTaxonomy,
  channel,
  hideLabel,
  displayAbove,
  placeholderText,
  ...others
}) => {
  const sendToPdp = useFeatureFlag('send_to_pdp_from_address_entry', { defaultValue: false });
  const impressionRef = React.useRef<string | undefined>();
  const { trackEvent } = useObservability();

  return (
    <>
      {!hideLabel && (
        <Label
          display="inline-block"
          mb="4"
          fontWeight="bold"
          color="neutrals0"
          id="address-input-label"
          fontSize="s0"
          htmlFor="address-input"
        >
          Enter your home address
        </Label>
      )}
      <AddressInput
        ariaLabelledby="address-input-label"
        aria-label="Enter your home address"
        className={className}
        onInputReady={() => {
          impressionRef.current = trackImpression({
            elementId: 'bricks:address-input',
            interactable: true,
            location: inputLocation,
            type: 'ready',
          }).impressionId;
        }}
        placeholderText={placeholderText ?? '125 W Muriel Dr'}
        onSubmit={(address: IAddressInputAddress) => {
          onSubmit(
            address,
            !!sendToPdp,
            trackEvent,
            impressionRef.current,
            trackingTaxonomy,
            channel,
            trackingKeywords,
            inputLocation,
          );
        }}
        onChooseManualEntry={() => startManualAddressVerification(channel, trackingKeywords)}
        googleMapsApiKey={GOOGLE_MAPS_API_KEY}
        channel={channel}
        optionsDisplayProps={{ displayAbove: displayAbove }}
        {...others}
      />
    </>
  );
};
